import React, { useState, useEffect, useRef } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../config/keys';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  MapPin, 
  Home, 
  Building, 
  X,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader,
  ArrowUpRight,
  Filter,
  TrendingUp,
  Map,
  Store,
  Star,
  Info
} from 'lucide-react';

// Constants
const DEFAULT_CENTER = { lat: -12.046374, lng: -77.042793 }; // Lima, Perú
const DEFAULT_ZOOM = 7; // Zoom inicial más bajo para mostrar ciudades
const ZOOM_THRESHOLD = 10; // Umbral para cambiar entre ciudades y propiedades
const API_BASE_URL = 'https://backend-production-1786.up.railway.app'; // Backend URL
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev'; // Cloudflare R2 URL

// Main PropertyMap component
const PropertyMap = () => {
  // Main states
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('venta');
  const [propertyType, setPropertyType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [cities, setCities] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [mapBounds, setMapBounds] = useState(null);
  const [priceFilter, setPriceFilter] = useState({ min: 0, max: null });
  const [roomsFilter, setRoomsFilter] = useState('all');
  const [isMapReady, setIsMapReady] = useState(false);
  
  // States for expandable sections - MODIFICADO: ciudades cerradas por defecto en móvil
  const [showCitiesSection, setShowCitiesSection] = useState(window.innerWidth >= 768);
  const [showPropertiesSection, setShowPropertiesSection] = useState(true);
  const [showPropertyPopup, setShowPropertyPopup] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Nuevos estados para filtros de usuario
  const [isUserFilteredType, setIsUserFilteredType] = useState(false);
  const [isUserFilteredPrice, setIsUserFilteredPrice] = useState(false);
  const [isUserFilteredRooms, setIsUserFilteredRooms] = useState(false);

  // References
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const isDataLoading = useRef(false);
  const boundsChangedTimeoutRef = useRef(null);
  const mapIdleListenerRef = useRef(null);
  const propertiesCacheRef = useRef({});
  
  // Clear markers from map
  const clearMarkers = () => {
    if (markersRef.current.length > 0) {
      markersRef.current.forEach(marker => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = [];
    }

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
  };

  // Añadir función para limpiar el caché de propiedades cuando cambian filtros o zoom
  const clearPropertiesCache = () => {
    console.log('Limpiando caché de propiedades');
    propertiesCacheRef.current = {};
  };

  // Format bounds for API
  const formatBoundsForAPI = (bounds) => {
    if (!bounds) return null;
    
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    return `${ne.lat()},${ne.lng()},${sw.lat()},${sw.lng()}`;
  };
  // Fetch cities with property counts
  const fetchCities = async () => {
    if (isDataLoading.current) return [];
    
    try {
      isDataLoading.current = true;
      
      console.log('Fetching cities with property counts');
      
      // Try to fetch from API
      try {
        const response = await fetch(`${API_BASE_URL}/api/ciudades`);
        
        if (!response.ok) {
          throw new Error(`Error fetching cities: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data.success || !Array.isArray(data.cities)) {
          throw new Error('Invalid response format from cities API');
        }
        
        // Filter cities with valid coordinates
        const validCities = data.cities.filter(city => 
          city.latitude && city.longitude && 
          !isNaN(parseFloat(city.latitude)) && 
          !isNaN(parseFloat(city.longitude))
        );
        
        if (validCities.length === 0 && data.cities.length > 0) {
          console.warn(`Se encontraron ${data.cities.length} ciudades pero ninguna tiene coordenadas válidas`);
          console.log('Primera ciudad recibida:', data.cities[0]);
        }
        
        setCities(validCities);
        console.log(`Loaded ${validCities.length} cities with valid coordinates`);
        return validCities;
      } catch (apiError) {
        console.warn('Error en API de ciudades, usando topCities como respaldo:', apiError.message);
        
        // Si falla la API de ciudades, usar las ciudades principales como respaldo
        if (topCities.length > 0) {
          return topCities;
        }
        
        return [];
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    } finally {
      isDataLoading.current = false;
    }
  };

  // Fetch top cities with most properties
  const fetchTopCities = async () => {
    if (isDataLoading.current) return [];
    
    try {
      isDataLoading.current = true;
      console.log('Loading top cities...');
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/ciudades/top?limit=10`);
        
        if (!response.ok) {
          throw new Error(`Error loading top cities: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data.success || !Array.isArray(data.cities)) {
          throw new Error('Invalid response format from top cities API');
        }
        
        let fetchedCities = data.cities || [];
        
        // Verificar si las ciudades tienen coordenadas
        const citiesWithCoords = fetchedCities.filter(city => 
          city.latitude && city.longitude && 
          !isNaN(parseFloat(city.latitude)) && 
          !isNaN(parseFloat(city.longitude))
        );
        
        console.log(`De ${fetchedCities.length} ciudades, ${citiesWithCoords.length} tienen coordenadas válidas`);
        
        // Si faltan coordenadas y tenemos el servicio de geocodificación disponible
        if (citiesWithCoords.length < fetchedCities.length && window.google?.maps?.Geocoder) {
          console.log('Intentando geocodificar ciudades sin coordenadas...');
          
          const geocoder = new window.google.maps.Geocoder();
          const geocodePromises = [];
          
          // Procesar solo las ciudades sin coordenadas válidas
          const citiesToGeocode = fetchedCities.filter(city => 
            !city.latitude || !city.longitude || 
            isNaN(parseFloat(city.latitude)) || 
            isNaN(parseFloat(city.longitude))
          );
          
          for (const city of citiesToGeocode) {
            const geocodePromise = new Promise(resolve => {
              const address = `${city.ciudad}, ${city.pais}`;
              
              geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                  const location = results[0].geometry.location;
                  city.latitude = location.lat();
                  city.longitude = location.lng();
                  console.log(`Geocodificada: ${city.ciudad} -> ${city.latitude}, ${city.longitude}`);
                } else {
                  console.warn(`No se pudo geocodificar: ${city.ciudad}`);
                }
                resolve();
              });
            });
            
            geocodePromises.push(geocodePromise);
          }
          
          // Esperar a que todas las geocodificaciones terminen (con un timeout razonable)
          await Promise.race([
            Promise.all(geocodePromises),
            new Promise(resolve => setTimeout(resolve, 3000))
          ]);
          
          // Filtrar de nuevo después de geocodificar
          fetchedCities = fetchedCities.filter(city => 
            city.latitude && city.longitude && 
            !isNaN(parseFloat(city.latitude)) && 
            !isNaN(parseFloat(city.longitude))
          );
          
          console.log(`Después de geocodificar: ${fetchedCities.length} ciudades con coordenadas`);
        }
        
        setTopCities(fetchedCities);
        console.log(`Loaded ${fetchedCities.length} top cities from API`);
        
        return fetchedCities;
      } catch (apiError) {
        console.log('Error fetching top cities:', apiError.message);
        
        // Si falla la API, dejamos la lista vacía
        setTopCities([]);
        return [];
      }
    } catch (error) {
      console.error('Fatal error loading top cities:', error);
      return [];
    } finally {
      isDataLoading.current = false;
    }
  };

  // Fetch property pins using new API endpoint
  const fetchPropertyPins = async () => {
    if (isDataLoading.current || !googleMapRef.current || !isMapReady) return [];
    
    try {
      isDataLoading.current = true;
      setLoading(true);
      
      // Get current bounds
      const bounds = googleMapRef.current.getBounds();
      if (!bounds) {
        console.log('No map bounds available');
        return [];
      }
      
      // Format bounds for API
      const boundsParam = formatBoundsForAPI(bounds);
      
      // Build URL parameters - IMPORTANTE: mantener la operación correcta
      const params = new URLSearchParams({
        operacion: activeTab === 'venta' ? 'venta' : 'alquiler',
        bounds: boundsParam
      });
      
      if (propertyType !== 'all') {
        params.append('tipo', propertyType);
      }
      
      if (priceFilter.min > 0) {
        params.append('precioMin', priceFilter.min);
      }
      
      if (priceFilter.max) {
        params.append('precioMax', priceFilter.max);
      }
      
      if (roomsFilter !== 'all') {
        params.append('habitaciones', roomsFilter);
      }
      
      if (selectedCity) {
        params.append('ubicacion', selectedCity.ciudad);
      }
      
      // Crear una clave de caché para estos parámetros
      const cacheKey = `${activeTab}_${params.toString()}`;
      
      // Verificar si tenemos estos resultados en caché
      if (propertiesCacheRef.current[cacheKey]) {
        console.log(`Usando datos en caché para: ${cacheKey}`);
        setVisibleProperties(propertiesCacheRef.current[cacheKey]);
        setLoading(false);
        return propertiesCacheRef.current[cacheKey];
      }
      
      const endpoint = `${API_BASE_URL}/api/mapa/propiedades-combinadas?${params}`;
      console.log(`Fetching property pins: ${endpoint}`);
      
      // Fetch from API
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`Error fetching property pins: ${response.status}`);
      }
      
      const properties = await response.json();
      console.log(`Loaded ${properties.length} property pins from API`);
      
      if (properties.length === 0) {
        console.log('No se encontraron propiedades. Parámetros:', params.toString());
      }
      
      // Guardar en caché para futuras solicitudes
      propertiesCacheRef.current[cacheKey] = properties;
      
      setVisibleProperties(properties);
      return properties;
      
    } catch (error) {
      console.error('Error fetching property pins:', error);
      // Generar propiedades de respaldo
      const fallbackProperties = generateFallbackProperties(5);
      setVisibleProperties(fallbackProperties);
      return fallbackProperties;
    } finally {
      isDataLoading.current = false;
      setLoading(false);
    }
  };

  // Generate fallback properties for testing if API fails
  const generateFallbackProperties = (count) => {
    const fallbackProps = [];
    
    // Center point
    let centerLat = -12.046374; // Lima
    let centerLng = -77.042793; 
    
    if (googleMapRef.current) {
      const center = googleMapRef.current.getCenter();
      centerLat = center.lat();
      centerLng = center.lng();
    }
    
    // Property types
    const types = ['casa', 'departamento', 'terreno', 'local'];
    
    // Generate random properties around the center
    for (let i = 0; i < count; i++) {
      // Random coordinates nearby (within ~2km)
      const lat = centerLat + (Math.random() - 0.5) * 0.03;
      const lng = centerLng + (Math.random() - 0.5) * 0.03;
      
      // Random property data
      const type = types[Math.floor(Math.random() * types.length)];
      const isRental = activeTab === 'alquiler' ? true : Math.random() > 0.7;
      const price = isRental 
        ? Math.floor(Math.random() * 1500) + 500 
        : Math.floor(Math.random() * 500000) + 150000;
      
      fallbackProps.push({
        id: `test-${i}`,
        titulo: `${type.charAt(0).toUpperCase() + type.slice(1)} en ${isRental ? 'alquiler' : 'venta'}`,
        tipo: type,
        ubicacion: 'Lima, Perú',
        precio: price.toString(),
        precio_dolares: Math.floor(price / 3.7).toString(),
        lat,
        lng,
        imagen: `/img/property${Math.floor(Math.random() * 5) + 1}.jpg`,
        habitaciones: Math.floor(Math.random() * 4) + 1,
        banos: Math.floor(Math.random() * 3) + 1,
        area_total: Math.floor(Math.random() * 150) + 50,
        operacion: isRental ? 'alquiler' : 'venta'
      });
    }
    
    return fallbackProps;
  };

  // Nueva función para encontrar la ciudad cercana al centro del mapa
  const findCityAtLocation = (center) => {
    if (!cities || cities.length === 0) return;
    
    console.log('Buscando ciudad cercana a', center);
    
    // Calcular la distancia desde el centro a cada ciudad
    let closestCity = null;
    let minDistance = Infinity;
    
    cities.forEach(city => {
      if (!city.latitude || !city.longitude) return;
      
      const cityLat = parseFloat(city.latitude);
      const cityLng = parseFloat(city.longitude);
      
      if (isNaN(cityLat) || isNaN(cityLng)) return;
      
      // Cálculo simple de distancia euclidiana (suficiente para comparación)
      const distance = Math.sqrt(
        Math.pow(center.lat - cityLat, 2) + 
        Math.pow(center.lng - cityLng, 2)
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        closestCity = city;
      }
    });
    
    // Si encontramos una ciudad cercana y está dentro de un radio razonable
    // (0.1 es aproximadamente 11km en el ecuador)
    if (closestCity && minDistance < 0.1) {
      console.log(`Ciudad encontrada cerca: ${closestCity.ciudad}, distancia: ${minDistance}`);
      
      // Solo seleccionar automáticamente si no hay ciudad ya seleccionada
      if (!selectedCity) {
        setSelectedCity(closestCity);
      }
    }
  };

  // Nueva función para refinar filtros basados en nivel de zoom
  const refineFiltersBasedOnZoom = (zoom) => {
    // A mayor zoom, podemos mostrar filtros más específicos
    if (zoom >= 16) {
      // En zoom muy cercano, podemos mostrar todas las propiedades
      // independiente del tipo
      if (propertyType !== 'all' && !isUserFilteredType) {
        setPropertyType('all');
      }
    } 
    // Nota: Se han eliminado los filtros automáticos para tipo de propiedad 'casa'
    // y límite de precio de 800,000 que se aplicaban anteriormente en zoom 12-14
  };

  // Create city marker - MODIFICADO para tamaño más grande
  const createCityMarker = (city) => {
    if (!window.google || !googleMapRef.current || !city) return null;
  
    // Analizar los valores directamente para depurar problemas de tipo
    console.log(`Creando marcador para ciudad: ${city.ciudad}`);
    console.log(`Valores originales - latitude: ${city.latitude}, longitude: ${city.longitude}`);
    console.log(`Tipos - latitude: ${typeof city.latitude}, longitude: ${typeof city.longitude}`);
  
    // Intentar parsear con diferentes métodos
    let lat, lng;
    
    // Intentar método 1: parseFloat directo
    lat = parseFloat(city.latitude);
    lng = parseFloat(city.longitude);
    
    // Si falla, intentar método 2: convertir a string primero
    if (isNaN(lat) || isNaN(lng)) {
      lat = parseFloat(String(city.latitude));
      lng = parseFloat(String(city.longitude));
      console.log(`Método 2 - lat: ${lat}, lng: ${lng}`);
    }
    
    // Verificar si tenemos valores numéricos válidos
    if (isNaN(lat) || isNaN(lng)) {
      console.warn(`No se pudieron extraer coordenadas válidas para: ${city.ciudad}`);
      return null;
    }
    
    // Verificar rango de valores (coordenadas válidas)
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.warn(`Coordenadas fuera de rango para: ${city.ciudad} (${lat}, ${lng})`);
      return null;
    }
  
    const position = { lat, lng };
    console.log(`Posición final: ${JSON.stringify(position)}`);
    
    // Escalar marcador basado en número de propiedades
    const baseSize = 14;
    const maxSize = 32;
    // Escala logarítmica para distribuir mejor los tamaños
    const scale = Math.max(Math.min(baseSize + Math.log10(city.n_propiedades) * 5, maxSize), baseSize);
    
    // Crear el SVG personalizado para ciudad
    const svgMarker = {
      path: 'M24,0C10.7,0,0,10.7,0,24s10.7,24,24,24s24-10.7,24-24S37.3,0,24,0z M24,36c-6.6,0-12-5.4-12-12s5.4-12,12-12 s12,5.4,12,12S30.6,36,24,36z',
      fillColor: '#4f46e5',
      fillOpacity: 0.9,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: scale/48, // Ajustar para el tamaño del SVG base (48x48)
      anchor: new window.google.maps.Point(24, 24)
    };
  
    // Crear marcador
    try {
      const marker = new window.google.maps.Marker({
        position,
        map: googleMapRef.current,
        title: `${city.ciudad}, ${city.pais} (${city.n_propiedades} propiedades)`,
        icon: svgMarker,
        label: {
          text: city.n_propiedades.toString(),
          color: '#FFFFFF',
          fontSize: '13px',
          fontWeight: 'bold'
        },
        zIndex: 5
      });
  
      // Info window content mejorado
      const infoContent = `
        <div style="padding: 18px; max-width: 280px; font-family: 'Inter', system-ui, sans-serif;">
          <h3 style="margin-top: 0; font-weight: 600; color: #1e293b; font-size: 18px; border-bottom: 2px solid #4f46e5; padding-bottom: 8px;">${city.ciudad}, ${city.pais}</h3>
          <div style="display: flex; align-items: center; margin: 12px 0;">
            <div style="background: linear-gradient(to right, #4f46e5, #3b82f6); color: white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 12px;">
              ${city.n_propiedades > 999 ? '999+' : city.n_propiedades}
            </div>
            <p style="color: #4f46e5; font-size: 16px; font-weight: 600; margin: 0;">
              ${city.n_propiedades} propiedades disponibles
            </p>
          </div>
          <button id="viewCity${city.id}" style="background: linear-gradient(to right, #4f46e5, #3b82f6); color: white; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; width: 100%; font-weight: 500; margin-top: 10px; box-shadow: 0 4px 12px -2px rgba(79, 70, 229, 0.3); transition: all 0.2s ease;">Ver propiedades</button>
        </div>
      `;
  
      const infoWindow = new window.google.maps.InfoWindow({
        content: infoContent,
        maxWidth: 300,
        ariaLabel: city.ciudad
      });
  
      // Click event
      marker.addListener('click', () => {
        // Close any open info window
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
  
        // Open this info window
        infoWindow.open({
          anchor: marker,
          map: googleMapRef.current
        });
        
        infoWindowRef.current = infoWindow;
  
        // Button event
        setTimeout(() => {
          const viewBtn = document.getElementById(`viewCity${city.id}`);
          if (viewBtn) {
            viewBtn.addEventListener('click', () => {
              // Navigate to city view
              googleMapRef.current.setCenter(position);
              googleMapRef.current.setZoom(14);
              setSelectedCity(city);
              
              // If search results are open, close them
              setShowSearchResults(false);
            });
          }
        }, 100);
      });
  
      // Save reference
      markersRef.current.push(marker);
      console.log(`Marcador creado correctamente para: ${city.ciudad}`);
      return marker;
    } catch (error) {
      console.error(`Error al crear marcador para ${city.ciudad}:`, error);
      return null;
    }
  };
  // Create property marker - MODIFICADO para tamaño más grande
  const createPropertyMarker = (property) => {
    if (!window.google || !googleMapRef.current || !property) return null;
  
    // Depurar valores de coordenadas
    console.log(`Creando marcador para propiedad: ${property.id}`);
    console.log(`Coordenadas originales - lat: ${property.lat}, lng: ${property.lng}`);
    
    // Extract coordinates - con manejo de errores mejorado
    let lat, lng;
    
    // Intentar obtener lat/lng como números
    if (typeof property.lat === 'number' && typeof property.lng === 'number') {
      lat = property.lat;
      lng = property.lng;
    } 
    // Si son strings, parsear
    else if (typeof property.lat === 'string' && typeof property.lng === 'string') {
      lat = parseFloat(property.lat);
      lng = parseFloat(property.lng);
    }
    // Intentar con latitude/longitude si lat/lng no están disponibles
    else if (property.latitude && property.longitude) {
      lat = parseFloat(property.latitude);
      lng = parseFloat(property.longitude);
      console.log(`Usando latitude/longitude alternativo: ${lat}, ${lng}`);
    }
    
    // Validar coordenadas
    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
      console.warn(`Propiedad sin coordenadas válidas: ${property.id}`);
      return null;
    }
    
    // Verificar rango de coordenadas
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.warn(`Coordenadas fuera de rango para: ${property.id} (${lat}, ${lng})`);
      return null;
    }
  
    const position = { lat, lng };
    const isRental = property.operacion === 'alquiler';
    const price = property.precio;
    
    // Determinar tipo de propiedad para icono especializado
    const getIconPathByType = (propertyType) => {
      switch(propertyType.toLowerCase()) {
        case 'casa':
          return 'M32,4L4,28v4h6v12h12V32h8v12h12V32h6v-4L32,4z M32,23.17L22.17,33H32V23.17z'; // Icono de casa
        case 'departamento':
          return 'M44,36V12L32,4L20,12v24H44z M36,32h-8v-8h8V32z M36,20h-8v-8h8V20z'; // Icono de edificio/departamento
        case 'terreno':
          return 'M8,36v-6.75L16,18l10,10l16-16v24H8z M40,12c0,2.21-1.79,4-4,4s-4-1.79-4-4s1.79-4,4-4S40,9.79,40,12z'; // Icono de terreno
        case 'local':
          return 'M42,18H6v24h36V18z M28,34H12v-4h16V34z M40,22H8v-2h32V22z'; // Icono de local comercial
        default:
          return 'M24,4c-7.73,0-14,6.27-14,14c0,10.5,14,26,14,26s14-15.5,14-26C38,10.27,31.73,4,24,4z M24,23c-2.76,0-5-2.24-5-5s2.24-5,5-5s5,2.24,5,5S26.76,23,24,23z'; // Pin por defecto
      }
    };
  
    // Determine marker color based on property type and operation
    const markerColor = isRental ? '#3b82f6' : '#4f46e5'; // blue for rental, indigo for sale
    
    // Get icon path based on property type
    const iconPath = getIconPathByType(property.tipo || 'default');
    
    // Create custom SVG marker with shadow and más elegante
    const svgMarker = {
      path: iconPath,
      fillColor: markerColor,
      fillOpacity: 0.9,
      strokeWeight: 1.5,
      strokeColor: '#ffffff',
      scale: 0.9,
      anchor: new window.google.maps.Point(24, 42),
      labelOrigin: new window.google.maps.Point(24, 52)
    };
  
    // Create marker
    const marker = new window.google.maps.Marker({
      position,
      map: googleMapRef.current,
      title: property.titulo,
      animation: window.google.maps.Animation.DROP,
      icon: svgMarker,
      label: {
        text: `S/ ${Number(price).toLocaleString()}`,
        color: '#ffffff',
        fontSize: '13px',
        fontWeight: 'bold',
        className: 'property-price-label'
      },
      zIndex: 10
    });
    
    // Info window content mejorado con más detalles y mejor visual
    const infoContent = `
      <div style="padding: 0; max-width: 320px; font-family: 'Inter', system-ui, sans-serif; border-radius: 12px; overflow: hidden;">
        <div style="position: relative;">
          <img src="${(() => {
            const imageUrl = Array.isArray(property.imagenes) && property.imagenes.length > 0
              ? property.imagenes[0]
              : property.imagen;
            if (!imageUrl) return '/default-property-image.jpg';
            if (imageUrl.startsWith('http')) return imageUrl;
            return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
          })()}" 
            alt="${property.titulo}" 
            style="width: 100%; height: 160px; object-fit: cover;"
            onerror="this.src='/default-property-image.jpg'">
          <div style="position: absolute; top: 12px; right: 12px; background-color: ${isRental ? '#3b82f6' : '#4f46e5'}; color: white; padding: 4px 10px; border-radius: 20px; font-weight: 500; font-size: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
            ${isRental ? 'Alquiler' : 'Venta'}
          </div>
        </div>
        
        <div style="padding: 16px;">
          <h3 style="margin-top: 0; font-weight: 600; font-size: 18px; color: #1e293b;">${property.titulo}</h3>
          
          <div style="display: flex; align-items: center; margin: 12px 0;">
            <div style="background: ${isRental ? 'linear-gradient(to right, #3b82f6, #60a5fa)' : 'linear-gradient(to right, #4f46e5, #4338ca)'}; color: white; padding: 8px 12px; border-radius: 8px; font-weight: 600; font-size: 16px;">
              S/ ${Number(price).toLocaleString()}${isRental ? '/mes' : ''}
            </div>
            ${property.precio_dolares && !isRental ? 
              `<div style="margin-left: 8px; background: #f8fafc; color: #64748b; padding: 8px 12px; border-radius: 8px; font-weight: 500; font-size: 16px;">
                $${Number(property.precio_dolares).toLocaleString()}
               </div>` 
              : ''
            }
          </div>
          
          <div style="display: flex; justify-content: space-between; margin: 16px 0; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; padding: 12px 0;">
            <div style="text-align: center;">
              <p style="margin: 0; font-weight: 600; font-size: 16px; color: #334155;">${property.habitaciones || '0'}</p>
              <p style="margin: 4px 0 0; font-size: 12px; color: #64748b;">Habitaciones</p>
            </div>
            
            <div style="text-align: center;">
              <p style="margin: 0; font-weight: 600; font-size: 16px; color: #334155;">${property.banos || '0'}</p>
              <p style="margin: 4px 0 0; font-size: 12px; color: #64748b;">Baños</p>
            </div>
            
            <div style="text-align: center;">
              <p style="margin: 0; font-weight: 600; font-size: 16px; color: #334155;">${property.area_total || '0'}</p>
              <p style="margin: 4px 0 0; font-size: 12px; color: #64748b;">m²</p>
            </div>
          </div>
          
          <p style="margin: 6px 0; font-size: 14px; color: #64748b; display: flex; align-items: center;">
            <span style="display: inline-block; width: 18px; height: 18px; background-color: #f1f5f9; border-radius: 50%; margin-right: 6px; text-align: center; line-height: 18px;">📍</span>
            ${property.ubicacion || ''}
          </p>
          
          <button id="viewProperty${property.id}" 
            style="background: ${isRental ? 
              'linear-gradient(to right, #3b82f6, #60a5fa)' : 
              'linear-gradient(to right, #4f46e5, #4338ca)'}; 
            color: white; border: none; padding: 12px 16px; border-radius: 10px; cursor: pointer; 
            margin-top: 16px; width: 100%; font-weight: 500; transition: all 0.2s ease; 
            box-shadow: 0 4px 12px -2px rgba(${isRental ? '59, 130, 246' : '79, 70, 229'}, 0.3);
            display: flex; justify-content: center; align-items: center; font-size: 15px;">
            Ver detalle completo
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-left: 8px;">
              <line x1="7" y1="17" x2="17" y2="7"></line>
              <polyline points="7 7 17 7 17 17"></polyline>
            </svg>
          </button>
        </div>
      </div>
    `;
  
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent,
      maxWidth: 320,
      ariaLabel: property.titulo
    });
  
    // Click event
    marker.addListener('click', () => {
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
  
      // Open this info window
      infoWindow.open({
        anchor: marker,
        map: googleMapRef.current
      });
      
      infoWindowRef.current = infoWindow;
      setSelectedProperty(property);
      setShowPropertyPopup(true);
  
      // Button event - CORREGIDO: URLs dinámicas basadas en operación
      setTimeout(() => {
        const viewBtn = document.getElementById(`viewProperty${property.id}`);
        if (viewBtn) {
          viewBtn.addEventListener('click', () => {
            // Navigate to property detail - URL corregida según operación
            const propertyUrl = property.operacion === 'alquiler' ? `/alquiler/${property.id}` : `/propiedad/${property.id}`;
            window.location.href = propertyUrl;
          });
        }
      }, 100);
    });
  
    // Save reference
    markersRef.current.push(marker);
    return marker;
  };

  // Show city markers
  const showCityMarkers = async () => {
    if (!googleMapRef.current || !isMapReady) return;
    
    console.log('Intentando mostrar marcadores de ciudades...');
    clearMarkers();
    
    try {
      // Get cities data
      const cities = await fetchCities();
      
      // Create markers for cities
      if (Array.isArray(cities) && cities.length > 0) {
        console.log(`Creando marcadores para ${cities.length} ciudades`);
        
        cities.forEach(city => {
          if (city) {
            // Verificación detallada de las coordenadas
            console.log(`Ciudad: ${city.ciudad}, Lat: ${city.latitude}, Lng: ${city.longitude}`);
            
            if (!city.latitude || !city.longitude || 
                isNaN(parseFloat(city.latitude)) || 
                isNaN(parseFloat(city.longitude))) {
              console.warn(`Coordenadas inválidas para ciudad: ${city.ciudad}`);
              return;
            }
            
            const marker = createCityMarker(city);
            if (marker) {
              console.log(`Marcador creado para: ${city.ciudad}`);
            } else {
              console.warn(`No se pudo crear marcador para: ${city.ciudad}`);
            }
          }
        });
      } else {
        // Si no hay ciudades disponibles, usar ciudades principales como respaldo
        if (topCities.length > 0) {
          console.log(`Usando ${topCities.length} ciudades principales como respaldo`);
          topCities.forEach(city => {
            if (city && city.latitude && city.longitude) {
              const marker = createCityMarker(city);
              if (marker) {
                console.log(`Marcador creado para ciudad principal: ${city.ciudad}`);
              }
            }
          });
        } else {
          console.warn('No hay ciudades disponibles para mostrar marcadores');
        }
      }
    } catch (error) {
      console.error('Error showing city markers:', error);
    }
  };

  // Show property markers
  const showPropertyMarkers = async () => {
    if (!googleMapRef.current || !isMapReady) return;
    
    console.log('Mostrando marcadores de propiedades...');
    
    // Limpiar marcadores existentes
    clearMarkers();
    
    // Mostrar indicador de carga
    setLoading(true);
    
    try {
      // Obtener propiedades
      const properties = await fetchPropertyPins();
      
      // Si no hay propiedades, mostrar mensaje y salir
      if (!Array.isArray(properties) || properties.length === 0) {
        console.log('No se encontraron propiedades para mostrar marcadores');
        setLoading(false);
        return;
      }
      
      console.log(`Creando marcadores para ${properties.length} propiedades`);
      
      // Crear marcadores para cada propiedad
      let markersCreated = 0;
      properties.forEach(property => {
        if (property) {
          const marker = createPropertyMarker(property);
          if (marker) {
            markersCreated++;
          }
        }
      });
      
      console.log(`Se crearon ${markersCreated} marcadores de propiedades`);
      
    } catch (error) {
      console.error('Error mostrando marcadores de propiedades:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update markers based on zoom level - MODIFICADO para incluir filtros
  const updateMarkersBasedOnZoom = () => {
    if (!googleMapRef.current) return;
    
    const currentZoom = googleMapRef.current.getZoom();
    console.log(`Zoom actual: ${currentZoom}, umbral: ${ZOOM_THRESHOLD}`);
    
    // Actualizar el estado de zoomLevel
    const prevZoom = zoomLevel;
    setZoomLevel(currentZoom);
    
    // Por debajo del umbral, mostrar ciudades
    if (currentZoom < ZOOM_THRESHOLD) {
      console.log('Zoom bajo, mostrando ciudades');
      showCityMarkers();
    } 
    // Por encima del umbral, mostrar propiedades
    else {
      console.log('Zoom alto, mostrando propiedades');
      
      // Si acabamos de cruzar el umbral (de zoom bajo a alto), 
      // podríamos ajustar los filtros automáticamente
      if (prevZoom < ZOOM_THRESHOLD && currentZoom >= ZOOM_THRESHOLD) {
        // Obtener el centro del mapa actual
        const center = googleMapRef.current.getCenter();
        const mapCenter = { 
          lat: center.lat(), 
          lng: center.lng() 
        };
        
        // Intentar determinar la ciudad actual basada en el centro del mapa
        findCityAtLocation(mapCenter);
        
        // Al hacer zoom a un área específica, considerar refinar
        // los filtros automáticamente
        refineFiltersBasedOnZoom(currentZoom);
      }
      
      // Mostrar propiedades con los filtros actualizados
      showPropertyMarkers();
    }
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    try {
      console.log(`Searching for: ${searchQuery}`);
      
      // Try API first
      try {
        const response = await fetch(`${API_BASE_URL}/api/ciudades/search?q=${encodeURIComponent(searchQuery)}`);
        
        if (!response.ok) {
          throw new Error(`Search error: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && Array.isArray(data.cities)) {
          setSearchResults(data.cities);
          setShowSearchResults(true);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (apiError) {
        console.log('Using mock search:', apiError.message);
        
        // No search results
        setSearchResults([]);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    }
  };

  // Navigate to location
  const navigateToLocation = (location) => {
    if (!googleMapRef.current) return;
    
    let position;
    
    // Check if location has latitude/longitude
    if (location.latitude && location.longitude) {
      position = { 
        lat: parseFloat(location.latitude), 
        lng: parseFloat(location.longitude) 
      };
    } else if (location.lat && location.lng) {
      position = {
        lat: parseFloat(location.lat),
        lng: parseFloat(location.lng)
      };
    }
    
    if (position && !isNaN(position.lat) && !isNaN(position.lng)) {
      googleMapRef.current.setCenter(position);
      
      // Si es una ciudad, zoom moderado (14)
      // Si es una propiedad, zoom cercano (16)
      const zoomLevel = location.ciudad ? 14 : 16;
      googleMapRef.current.setZoom(zoomLevel);
      
      // Si es una propiedad, selecciónarla
      if (location.titulo) {
        setSelectedProperty(location);
        setShowPropertyPopup(true);
      } 
      // Si es una ciudad, seleccionarla
      else if (location.ciudad) {
        setSelectedCity(location);
      }
      
      setShowSearchResults(false);
    } else {
      console.log('Invalid coordinates, attempting geocode');
      
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        const address = location.ciudad 
          ? `${location.ciudad}, ${location.pais}` 
          : location.ubicacion || '';
          
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const mapLocation = results[0].geometry.location;
            googleMapRef.current.setCenter(mapLocation);
            googleMapRef.current.setZoom(14);
            
            if (location.ciudad) {
              setSelectedCity(location);
            }
          }
        });
      }
    }
  };

  // Handle tab change with improved logic
  const handleTabChange = (tab) => {
    // Si es el mismo tab, no hacer nada
    if (tab === activeTab) return;
    
    console.log(`Cambiando de ${activeTab} a ${tab}`);
    
    // Actualizar estado
    setActiveTab(tab);
    
    // Limpiar marcadores para asegurar una actualización completa
    clearMarkers();
    
    // Limpiar caché de propiedades al cambiar el modo
    clearPropertiesCache();
    
    // Actualizar las propiedades visibles basadas en el nuevo modo
    if (isMapReady && googleMapRef.current) {
      // Forzar una pequeña espera para asegurar que el estado se actualiza
      setTimeout(() => {
        const zoom = googleMapRef.current.getZoom();
        
        // Si estamos en un zoom alto, mostrar propiedades directamente
        if (zoom >= ZOOM_THRESHOLD) {
          showPropertyMarkers();
        } else {
          // En zoom bajo, mostrar ciudades
          showCityMarkers();
        }
      }, 50);
    }
  };

  // Modificar la función handlePropertyTypeChange para marcar filtros de usuario
  const handlePropertyTypeChange = (type) => {
    setPropertyType(type);
    setIsUserFilteredType(true);
    
    // Limpiar caché de propiedades al cambiar el filtro de tipo
    clearPropertiesCache();
  };

  // Function to handle price filter change
  const handlePriceFilterChange = (min, max) => {
    setPriceFilter({ min, max });
    setIsUserFilteredPrice(true);
    clearPropertiesCache();
  };

  // Function to handle rooms filter change
  const handleRoomsFilterChange = (rooms) => {
    setRoomsFilter(rooms);
    setIsUserFilteredRooms(true);
    clearPropertiesCache();
  };

  // Load Google Maps script - CORREGIDO: prevent zoom on page
  useEffect(() => {
    // Avoid loading multiple times
    if (scriptLoadedRef.current) return;
    scriptLoadedRef.current = true;
    
    // NUEVO: Prevenir zoom de página
    const preventPageZoom = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    
    // NUEVO: Prevenir zoom con scroll del mouse
    const preventWheelZoom = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('keydown', preventPageZoom);
    document.addEventListener('wheel', preventWheelZoom, { passive: false });
    
    // Create custom price label style
    const style = document.createElement('style');
    style.id = 'property-price-style';
    style.innerHTML = `
      body {
        zoom: 1 !important;
        transform: scale(1) !important;
        transform-origin: top left !important;
      }
      .property-price-label {
        font-family: 'Inter', system-ui, sans-serif;
        font-weight: bold;
        background: linear-gradient(to right, rgba(79, 70, 229, 0.95), rgba(59, 130, 246, 0.95));
        padding: 4px 8px;
        border-radius: 20px;
        border: 1.5px solid white;
        font-size: 13px;
        box-shadow: 0 3px 6px rgba(0,0,0,0.2);
        min-width: 80px;
        text-align: center;
      }
    `;
    document.head.appendChild(style);
    
    // Global callback for initializing the map
    window.initMap = function() {
      initializeMap();
    };
    
    // Load Google Maps script if not already loaded
    if (window.google && window.google.maps) {
      initializeMap();
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initMap`;
      script.async = true;
      script.defer = true;
      
      script.onerror = () => {
        console.error('Error loading Google Maps API');
        setLoading(false);
      };
      
      document.head.appendChild(script);
    }
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', preventPageZoom);
      document.removeEventListener('wheel', preventWheelZoom);
      
      if (window.initMap) {
        delete window.initMap;
      }
      
      if (document.getElementById('property-price-style')) {
        document.getElementById('property-price-style').remove();
      }
      
      clearMarkers();
      
      if (mapIdleListenerRef.current && window.google) {
        window.google.maps.event.removeListener(mapIdleListenerRef.current);
      }
    };
  }, []);

  // Initialize map - MEJORADO para respuesta al zoom y prevenir zoom de página
  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;
    
    try {
      // Create map
      const mapOptions = {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy', // NUEVO: mejor manejo de gestos
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_TOP
        },
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [
              { color: "#e2e8f0" }
            ]
          },
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [
              { color: "#f8fafc" }
            ]
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [
              { color: "#e0e7ff" }
            ]
          },
          {
            featureType: "road.arterial",
            elementType: "geometry",
            stylers: [
              { color: "#c7d2fe" }
            ]
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [
              { color: "#a5b4fc" }
            ]
          }
        ]
      };
      
      const map = new window.google.maps.Map(mapRef.current, mapOptions);
      googleMapRef.current = map;
      
      // Store current bounds
      const bounds = map.getBounds();
      setMapBounds(bounds);
      
      // Mejorar listener de zoom para ser más responsive
      map.addListener('zoom_changed', () => {
        const newZoom = map.getZoom();
        console.log(`Zoom cambiado a: ${newZoom}`);
        
        // Actualizar estado de zoom inmediatamente
        setZoomLevel(newZoom);
        
        // Limpiar el timeout existente si hay uno
        if (boundsChangedTimeoutRef.current) {
          clearTimeout(boundsChangedTimeoutRef.current);
        }
        
        // Establecer un corto retraso antes de actualizar marcadores
        // para prevenir muchas llamadas durante zoom continuo
        boundsChangedTimeoutRef.current = setTimeout(() => {
          updateMarkersBasedOnZoom();
        }, 250); // Reducido de 500ms a 250ms para mayor responsividad
      });
      
      // Map events - throttled update to prevent too many API calls
      if (mapIdleListenerRef.current) {
        window.google.maps.event.removeListener(mapIdleListenerRef.current);
      }
      
      mapIdleListenerRef.current = map.addListener('idle', () => {
        if (boundsChangedTimeoutRef.current) {
          clearTimeout(boundsChangedTimeoutRef.current);
        }
        
        boundsChangedTimeoutRef.current = setTimeout(() => {
          setMapBounds(map.getBounds());
          updateMarkersBasedOnZoom();
        }, 250); // Reducido de 500ms a 250ms para mayor responsividad
      });
      
      // Load top cities for sidebar
      fetchTopCities().then(cities => {
        // Asegurar que hay ciudades principales antes de mostrar marcadores
        if (cities && cities.length > 0) {
          // Forzar mostrar ciudades inicialmente, independiente del zoom
          setTimeout(() => {
            showCityMarkers();
          }, 500);
        } else {
          // Si no hay ciudades principales, mostrar marcadores según zoom
          setTimeout(() => {
            updateMarkersBasedOnZoom();
          }, 500);
        }
      });
      
      // Mark map as ready
      setIsMapReady(true);
      setLoading(false);
      
    } catch (error) {
      console.error('Error initializing map:', error);
      setLoading(false);
    }
  };

  // Update when filters change
  useEffect(() => {
    if (googleMapRef.current && isMapReady) {
      console.log('Filtros cambiados, actualizando marcadores...');
      
      // Limpiar propiedades visibles
      setVisibleProperties([]);
      clearMarkers();
      
      // Limpiar caché al cambiar filtros
      clearPropertiesCache();
      
      // Pequeña espera para asegurar que los estados se han actualizado
      setTimeout(() => {
        // Decidir qué mostrar basado en el nivel de zoom
        const currentZoom = googleMapRef.current.getZoom();
        if (currentZoom >= ZOOM_THRESHOLD) {
          showPropertyMarkers();
        } else {
          showCityMarkers();
        }
      }, 50);
    }
  }, [activeTab, propertyType, priceFilter, roomsFilter, isMapReady]);

  // NUEVO: Efecto para manejar cambios de tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && showCitiesSection) {
        setShowCitiesSection(false);
      } else if (!isMobile && !showCitiesSection) {
        setShowCitiesSection(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showCitiesSection]);

  // Añadir nuevo efecto para vigilar cambios en el nivel de zoom
  useEffect(() => {
    if (isMapReady && googleMapRef.current) {
      // Actualizar UI basada en nivel de zoom
      if (zoomLevel >= ZOOM_THRESHOLD) {
        // Mostrar mensaje basado en nivel de zoom
        if (zoomLevel >= 16) {
          console.log('Zoom muy cercano - mostrando todas las propiedades en detalle');
        } else if (zoomLevel >= 14) {
          console.log('Zoom cercano - enfocando en propiedades específicas');
        } else {
          console.log('Zoom medio - mostrando propiedades en la zona');
        }
      } else {
        console.log('Zoom alejado - mostrando ciudades');
      }
    }
  }, [zoomLevel, isMapReady]);

  // Modificar el precio del filtro cuando se cambia el nivel de zoom
  useEffect(() => {
    if (!isUserFilteredPrice && isMapReady) {
      // Siempre mantener sin filtro de precio máximo predeterminado
      // a menos que el usuario lo ajuste manualmente
      setPriceFilter({ min: 0, max: null });
    }
  }, [zoomLevel, activeTab, isUserFilteredPrice, isMapReady]);

  // UI functions
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };  

  // Toggle expandable sections
  const togglePropertiesSection = () => {
    setShowPropertiesSection(!showPropertiesSection);
  };

  const toggleCitiesSection = () => {
    setShowCitiesSection(!showCitiesSection);
  };

  // Añadir un componente visual que muestre la información del nivel de zoom actual
  const ZoomLevelIndicator = () => {
    return (
      <motion.div
        className="absolute bottom-24 left-4 bg-white/90 backdrop-blur-sm rounded-full shadow-lg px-3 py-1.5 z-10"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <div className="bg-indigo-100 p-1 rounded-full">
            <Search size={14} className="text-indigo-600" />
          </div>
          <p className="text-sm font-medium">
            Zoom: <span className="text-indigo-600">{zoomLevel.toFixed(1)}</span>
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="relative flex h-screen w-full bg-white overflow-hidden">
      {/* Toggle Sidebar Button (Mobile) */}
      <button 
        className="absolute top-4 left-4 z-30 md:hidden bg-white p-2 rounded-full shadow-lg"
        onClick={toggleSidebar}
      >
        {showSidebar ? <X size={20} className="text-indigo-600" /> : <Filter size={20} className="text-indigo-600" />}
      </button>
      
      {/* Sidebar - MODIFICADO: mejor responsividad y altura fija */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            className="w-80 h-full bg-white shadow-xl z-20 flex flex-col absolute md:relative left-0 top-0 max-h-screen overflow-hidden"
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header - FIJO */}
            <div className="p-5 border-b border-indigo-100 bg-gradient-to-r from-indigo-600 to-blue-600 text-white flex-shrink-0">
              <h1 className="text-xl md:text-2xl font-bold mb-4">Explorador de Propiedades</h1>
              
              {/* Search */}
              <div className="relative">
                <div className="flex items-center rounded-lg overflow-hidden bg-white/10 focus-within:bg-white/20 transition-all border-2 border-white/30">
                  <input
                    type="text"
                    placeholder="Buscar ubicación..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full py-2 px-4 outline-none bg-transparent text-white placeholder-white/70"
                  />
                  <motion.button 
                    onClick={handleSearch}
                    className="bg-white text-indigo-600 p-2 h-full"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Search size={20} />
                  </motion.button>
                </div>
                
                {/* Search results */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white shadow-xl rounded-lg mt-2 z-50 max-h-60 overflow-y-auto">
                    {searchResults.map((result) => (
                      <motion.div 
                        key={result.id} 
                        className="p-3 hover:bg-indigo-50 cursor-pointer flex justify-between items-center border-b border-indigo-50 last:border-b-0"
                        onClick={() => navigateToLocation(result)}
                        whileHover={{ backgroundColor: "#eef2ff", x: 3 }}
                      >
                        <div>
                          <span className="font-medium text-gray-800">{result.ciudad || result.titulo}, </span>
                          <span className="text-gray-600">{result.pais || result.ubicacion}</span>
                        </div>
                        <div className="flex items-center">
                          {result.n_propiedades && (
                            <span className="text-indigo-600 font-medium mr-2">{result.n_propiedades}</span>
                          )}
                          <ChevronRight size={16} className="text-indigo-400" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                
                {showSearchResults && searchResults.length === 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white shadow-xl rounded-lg mt-2 z-50 p-4 text-center">
                    <p className="text-gray-600">No se encontraron resultados para "{searchQuery}"</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Tabs for sale/rental - FIJO */}
            <div className="flex mt-4 mx-4 rounded-lg overflow-hidden border border-indigo-100 shadow-sm flex-shrink-0">
              <motion.button
                className={`flex-1 py-3 font-medium text-sm md:text-base ${activeTab === 'venta' 
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-indigo-50'}`}
                onClick={() => handleTabChange('venta')}
                whileHover={activeTab !== 'venta' ? { backgroundColor: "#eef2ff" } : {}}
                whileTap={{ scale: 0.98 }}
              >
                <Home className="w-4 h-4 mr-1 md:mr-2 inline-block" />
                En Venta
              </motion.button>
              <motion.button
                className={`flex-1 py-3 font-medium text-sm md:text-base ${activeTab === 'alquiler' 
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-indigo-50'}`}
                onClick={() => handleTabChange('alquiler')}
                whileHover={activeTab !== 'alquiler' ? { backgroundColor: "#eef2ff" } : {}}
                whileTap={{ scale: 0.98 }}
              >
                <Building className="w-4 h-4 mr-1 md:mr-2 inline-block" />
                En Alquiler
              </motion.button>
            </div>
            
            {/* Property type filters - FIJO */}
            <div className="mt-4 mx-4 flex flex-wrap gap-2 flex-shrink-0">
              {[
                { id: 'all', label: 'Todos', icon: TrendingUp },
                { id: 'casa', label: 'Casas', icon: Home },
                { id: 'departamento', label: 'Deptos', icon: Building },
                { id: 'terreno', label: 'Terrenos', icon: Map },
                { id: 'local', label: 'Locales', icon: Store }
              ].map((type) => (
                <motion.button
                  key={type.id}
                  className={`px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm rounded-full flex items-center gap-1 ${propertyType === type.id
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-indigo-100 hover:bg-indigo-50'}`}
                  onClick={() => handlePropertyTypeChange(type.id)}
                  whileHover={propertyType !== type.id ? { scale: 1.05, backgroundColor: "#eef2ff" } : {}}
                  whileTap={{ scale: 0.95 }}
                >
                  <type.icon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                  {type.label}
                </motion.button>
              ))}
            </div>
            
            {/* CONTENIDO SCROLLEABLE */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Top cities list - Expandable section */}
              <div className="mt-4 mx-4 border-b border-indigo-100 pb-2">
                <div 
                  className="flex justify-between items-center cursor-pointer py-2" 
                  onClick={toggleCitiesSection}
                >
                  <h2 className="font-bold text-base md:text-lg text-indigo-800 flex items-center">
                    <MapPin className="mr-2 w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                    Ciudades populares
                  </h2>
                  <motion.div 
                    animate={{ rotate: showCitiesSection ? 0 : 180 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronUp className={`w-4 h-4 md:w-5 md:h-5 ${showCitiesSection ? 'text-indigo-600' : 'text-gray-400'}`} />
                  </motion.div>
                </div>
                
                <AnimatePresence>
                  {showCitiesSection && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-3 max-h-48 md:max-h-56 overflow-y-auto py-2">
                        {topCities.length > 0 ? (
                          topCities.map((city) => (
                            <motion.div 
                              key={`${city.id}`}
                              className="border border-indigo-100 rounded-xl p-3 hover:bg-indigo-50 cursor-pointer flex justify-between items-center shadow-sm"
                              onClick={() => navigateToLocation(city)}
                              whileHover={{ 
                                scale: 1.03, 
                                backgroundColor: "#eef2ff",
                                boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.1)" 
                              }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div>
                                <div className="font-medium text-gray-800 text-sm md:text-base">{city.ciudad}</div>
                                <div className="text-xs md:text-sm text-gray-600">{city.pais}</div>
                              </div>
                              <div className="flex items-center">
                                <span className="text-indigo-600 font-medium mr-2 text-sm md:text-base">{city.n_propiedades}</span>
                                <ChevronRight size={16} className="text-indigo-400" />
                              </div>
                            </motion.div>
                          ))
                        ) : (
                          <motion.div 
                            className="text-gray-500 text-center py-4 rounded-lg border border-dashed border-indigo-200"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                          >
                            <Loader className="w-6 h-6 text-indigo-400 mx-auto mb-2 animate-spin" />
                            <p className="text-sm">Cargando ciudades...</p>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Properties in current view - With direct Cloudflare R2 URL integration */}
              <div className="mt-4 mx-4 border-b border-indigo-100 pb-2">
                <div 
                  className="flex justify-between items-center cursor-pointer py-2" 
                  onClick={togglePropertiesSection}
                >
                  <h2 className="font-bold text-base md:text-lg text-indigo-800 flex items-center">
                    <Home className="mr-2 w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                    Propiedades en esta zona
                    {visibleProperties.length > 0 && (
                      <span className="ml-2 text-xs md:text-sm bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">
                        {visibleProperties.length}
                      </span>
                    )}
                  </h2>
                  <motion.div 
                    animate={{ rotate: showPropertiesSection ? 0 : 180 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronUp className={`w-4 h-4 md:w-5 md:h-5 ${showPropertiesSection ? 'text-indigo-600' : 'text-gray-400'}`} />
                  </motion.div>
                </div>
                
                <AnimatePresence>
                  {showPropertiesSection && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      {visibleProperties.length > 0 ? (
                        <div className="space-y-4 max-h-80 md:max-h-96 overflow-y-auto py-2">
                          {visibleProperties.slice(0, 5).map((property) => (
                            <motion.div 
                              key={property.id}
                              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all cursor-pointer border border-gray-200"
                              onClick={() => navigateToLocation(property)}
                              whileHover={{ scale: 1.02, y: -3 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="relative h-24 md:h-32">
                                {/* Direct image loading with Cloudflare R2 URL */}
                                <img 
                                  src={(() => {
                                    const imageUrl = Array.isArray(property.imagenes) && property.imagenes.length > 0
                                      ? property.imagenes[0]
                                      : property.imagen;
                                    
                                    if (!imageUrl) return '/default-property-image.jpg';
                                    if (imageUrl.startsWith('http')) return imageUrl;
                                    return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
                                  })()}
                                  alt={property.titulo || 'Propiedad'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.src = '/default-property-image.jpg'; }}
                                />
                                
                                {/* Badge showing operation type */}
                                <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded-lg text-xs font-bold text-blue-600">
                                  {property.operacion === 'alquiler' ? 'Alquiler' : 'Venta'}
                                </div>
                              </div>
                              
                              <div className="p-3">
                                {/* Property title */}
                                <h3 className="font-medium text-gray-800 line-clamp-1 text-sm md:text-base" title={property.titulo}>
                                  {property.titulo || 'Propiedad sin título'}
                                </h3>
                                
                                {/* Location */}
                                <p className="text-xs md:text-sm text-gray-500 line-clamp-1" title={property.ubicacion}>
                                  {property.ubicacion || 'Ubicación no especificada'}
                                </p>
                                
                                {/* Price and features */}
                                <div className="mt-2 flex justify-between items-center">
                                  <p className="font-bold text-indigo-600 text-sm md:text-base">
                                    S/ {property.operacion === 'alquiler' 
                                      ? (Number(property.precio_mensual || property.precio) || 0).toLocaleString() 
                                      : (Number(property.precio_soles || property.precio) || 0).toLocaleString()}
                                    {property.operacion === 'alquiler' ? '/mes' : ''}
                                  </p>
                                  
                                  <div className="flex items-center text-xs text-gray-500">
                                    {property.habitaciones && <span className="mr-1 md:mr-2">{property.habitaciones} hab</span>}
                                    {property.habitaciones && property.banos && <span>|</span>}
                                    {property.banos && <span className="mx-1 md:mx-2">{property.banos} baños</span>}
                                    {property.banos && property.area_total && <span>|</span>}
                                    {property.area_total && <span className="ml-1 md:ml-2">{property.area_total} m²</span>}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                          
                          {visibleProperties.length > 5 && (
                            <motion.button 
                              className="w-full py-2 text-sm text-indigo-600 font-medium flex items-center justify-center bg-white rounded-lg border border-indigo-100 hover:bg-indigo-50"
                              onClick={() => {
                                // Navigate to a listings page with current filters - URL CORREGIDA
                                const filterParams = new URLSearchParams({
                                  operacion: activeTab === 'venta' ? 'comprar' : 'alquiler',
                                  tipo: propertyType !== 'all' ? propertyType : '',
                                  ubicacion: selectedCity?.ciudad || ''
                                });
                                
                                // URL corregida según operación
                                const baseUrl = activeTab === 'venta' ? '/propiedades' : '/alquileres';
                                window.location.href = `${baseUrl}?${filterParams}`;
                              }}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              Ver todas <ArrowUpRight size={14} className="ml-1" />
                            </motion.button>
                          )}
                        </div>
                      ) : (
                        <div className="py-8 text-center">
                          {loading ? (
                            <motion.div 
                              className="flex flex-col items-center"
                              animate={{ opacity: [0.5, 1, 0.5] }}
                              transition={{ repeat: Infinity, duration: 1.5 }}
                            >
                              <Loader className="w-8 h-8 text-indigo-400 mb-2 animate-spin" />
                              <p className="text-gray-500 text-sm">Buscando propiedades...</p>
                            </motion.div>
                          ) : (
                            <div className="text-gray-500">
                              <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm">No hay propiedades disponibles en esta zona</p>
                              <p className="text-xs mt-1">Prueba ajustando los filtros o cambiando la ubicación</p>
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer - FIJO */}
            <div className="p-4 border-t border-indigo-100 bg-white flex-shrink-0">
              <div className="text-center text-sm text-indigo-600">
                {visibleProperties.length > 0 ? (
                  <>
                    <strong>{visibleProperties.length}</strong> propiedades encontradas
                    {selectedCity && (
                      <span className="ml-1">en <span className="font-semibold">{selectedCity.ciudad}</span></span>
                    )}
                  </>
                ) : (
                  <>
                    {loading ? 'Buscando propiedades...' : 'Cambia los filtros para ver propiedades'}
                  </>
                )}
                <div className="mt-2 text-xs text-gray-500 flex items-center justify-center gap-1">
                  <Info size={12} />
                  {zoomLevel < ZOOM_THRESHOLD ? 'Acerca el mapa para ver propiedades' : 'Haz clic en los marcadores para ver detalles'}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Map */}
      <div className="flex-1 relative overflow-hidden">
        {loading && (
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-blue-600/10 backdrop-blur-sm flex flex-col items-center justify-center z-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              animate={{ 
                rotate: 360,
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                rotate: { duration: 1.5, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
              className="bg-white p-4 rounded-full shadow-xl mb-4"
            >
              <Loader className="w-8 h-8 text-indigo-600" />
            </motion.div>
            <motion.span 
              className="text-lg font-medium text-white bg-indigo-600/70 px-4 py-2 rounded-lg"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Cargando propiedades...
            </motion.span>
          </motion.div>
        )}
        
        {/* Map container */}
        <div ref={mapRef} className="w-full h-full" />
        
        {/* Zoom level indicator - NUEVO */}
        {isMapReady && (
          <ZoomLevelIndicator />
        )}
        
        {/* Selected city banner */}
        <AnimatePresence>
          {selectedCity && (
            <motion.div 
              className="absolute top-4 left-4 bg-white rounded-xl shadow-xl p-4 z-10 max-w-xs"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", damping: 20 }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-bold text-lg text-gray-800">{selectedCity.ciudad}</h2>
                  <p className="text-gray-600">{selectedCity.pais}</p>
                </div>
                <motion.button 
                  onClick={() => setSelectedCity(null)}
                  className="text-gray-500 hover:text-indigo-600 bg-gray-100 p-1 rounded-full"
                  whileHover={{ scale: 1.1, backgroundColor: "#eef2ff" }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X size={16} />
                </motion.button>
              </div>
              <div className="mt-2">
                <p className="font-medium">
                  <span className="text-indigo-600">{selectedCity.n_propiedades}</span> propiedades disponibles
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Map legend */}
        <motion.div 
          className="absolute bottom-4 right-4 bg-white rounded-xl shadow-lg p-4 z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="mb-2 font-medium text-gray-700 text-sm">Leyenda:</div>
          
          {zoomLevel < ZOOM_THRESHOLD ? (
            // Leyenda para ciudades
            <div className="flex items-center mb-2">
              <div className="h-5 w-5 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold mr-2">
                12
              </div>
              <span className="text-sm text-gray-700">Número de propiedades en la ciudad</span>
            </div>
          ) : (
            // Leyenda para propiedades
            <>
              <div className="flex items-center mb-2">
                <div className="h-5 w-5 mr-2 relative">
                  <div className="absolute inset-0 bg-indigo-600 rounded-full"></div>
                  <div className="absolute -bottom-2 left-0 right-0 text-center text-xs font-bold bg-white border border-indigo-600 rounded-sm text-indigo-600 px-1">S/</div>
                </div>
                <span className="text-sm text-gray-700">Propiedad en venta</span>
              </div>
              
              <div className="flex items-center">
                <div className="h-5 w-5 mr-2 relative">
                  <div className="absolute inset-0 bg-blue-500 rounded-full"></div>
                  <div className="absolute -bottom-2 left-0 right-0 text-center text-xs font-bold bg-white border border-blue-500 rounded-sm text-blue-500 px-1">S/</div>
                </div>
                <span className="text-sm text-gray-700">Propiedad en alquiler</span>
              </div>
            </>
          )}
        </motion.div>

        {/* Zoom guidance message - MEJORADO */}
        <AnimatePresence>
          {zoomLevel < ZOOM_THRESHOLD && (
            <motion.div 
              className="absolute bottom-24 right-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-3 z-10 max-w-xs"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="flex items-center gap-2">
                <div className="bg-indigo-100 p-1 rounded-full">
                  <Search size={16} className="text-indigo-600" />
                </div>
                <p className="text-sm text-gray-700">
                  {zoomLevel < 10 
                    ? "Acerca el mapa para ver propiedades individuales"
                    : `Acerca un poco más (zoom ${ZOOM_THRESHOLD}) para ver propiedades`}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filtros activos */}
        <AnimatePresence>
          {(propertyType !== 'all' || priceFilter.max || roomsFilter !== 'all' || selectedCity) && (
            <motion.div 
              className="absolute top-4 right-4 bg-white rounded-xl shadow-lg p-3 z-10"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-wrap gap-2">
                {propertyType !== 'all' && (
                  <div className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-lg text-sm flex items-center">
                    <span>Tipo: {propertyType.charAt(0).toUpperCase() + propertyType.slice(1)}</span>
                    <button 
                      className="ml-1 text-indigo-600 hover:text-indigo-800"
                      onClick={() => handlePropertyTypeChange('all')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {priceFilter.max && (
                  <div className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-lg text-sm flex items-center">
                    <span>Hasta S/ {priceFilter.max.toLocaleString()}</span>
                    <button 
                      className="ml-1 text-indigo-600 hover:text-indigo-800"
                      onClick={() => handlePriceFilterChange(0, null)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {roomsFilter !== 'all' && (
                  <div className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-lg text-sm flex items-center">
                    <span>{roomsFilter}+ habitaciones</span>
                    <button 
                      className="ml-1 text-indigo-600 hover:text-indigo-800"
                      onClick={() => handleRoomsFilterChange('all')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mensaje cuando no hay resultados */}
        <AnimatePresence>
          {isMapReady && visibleProperties.length === 0 && zoomLevel >= ZOOM_THRESHOLD && !loading && (
            <motion.div 
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6 z-10 max-w-md text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No se encontraron propiedades</h3>
              <p className="text-gray-600 mb-4">
                No hay propiedades que coincidan con tus filtros en esta zona. 
                Prueba ajustando los filtros o explorando otra ubicación.
              </p>
              <button
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                onClick={() => {
                  setPropertyType('all');
                  setPriceFilter({ min: 0, max: null });
                  setRoomsFilter('all');
                  if (selectedCity) setSelectedCity(null);
                  
                  // Reset user filter flags
                  setIsUserFilteredType(false);
                  setIsUserFilteredPrice(false);
                  setIsUserFilteredRooms(false);
                  
                  // Clear cache
                  clearPropertiesCache();
                }}
              >
                Limpiar filtros
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Botón para alternar entre venta/alquiler en móvil */}
        <div className="md:hidden absolute bottom-20 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-white rounded-full shadow-lg flex overflow-hidden">
            <button 
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'venta' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700'
              }`}
              onClick={() => handleTabChange('venta')}
            >
              Venta
            </button>
            <button 
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'alquiler' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700'
              }`}
              onClick={() => handleTabChange('alquiler')}
            >
              Alquiler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyMap;